'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { X } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { formatImageURL, formatPrice, filterDiscounts, getEffectivePrice, getStrikethroughPrice, getLoyaltyProductDiscount, isDiscountBetter } from '@/utils/util'
import { useCart } from '@/lib/commerce/hooks'
import { panelFocusRing } from '../elements/SidePanel'
import { hasOptions } from '@/lib/commerce/variantOptions'
import { OptionQuickAdd } from './VariantOptions'

const rowFocus = 'focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-foreground'
const sweepOnGroup = 'pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] group-hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none'

export default function VariantSelectionModal({ isOpen, onClose, product, onAddToCart }: { isOpen: boolean, onClose: () => void, product: any, vendor?: any, onAddToCart: (variantId: string) => void }) {
  const { data: session } = useSession()
  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const variants = product?.variants || [product?.variant].filter(Boolean)

  const isAvailable = (variant: any) => !product?.isTrackable || variant.stock > 0

  const select = (variant: any) => {
    if (!isAvailable(variant)) return
    onAddToCart(variant.id)
    onClose()
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-foreground/30" />
        </TransitionChild>
        <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-8">
          <TransitionChild as={Fragment} enter="transition ease-out duration-300 sm:duration-200 motion-reduce:transition-none" enterFrom="translate-y-full sm:translate-y-2 sm:opacity-0" enterTo="translate-y-0 sm:opacity-100" leave="transition ease-in duration-200 sm:duration-150 motion-reduce:transition-none" leaveFrom="translate-y-0 sm:opacity-100" leaveTo="translate-y-full sm:translate-y-2 sm:opacity-0">
            <DialogPanel className="w-full sm:max-w-lg flex flex-col max-h-[85dvh] sm:max-h-[calc(100dvh-4rem)] bg-background text-foreground">
              <div className="flex items-start justify-between gap-6 px-4 sm:px-8 pt-6 shrink-0">
                <div className="min-w-0">
                  <DialogTitle className="text-xl sm:text-2xl tracking-tight">Төрөл сонгох</DialogTitle>
                  <p className="mt-1 truncate text-[13px] text-muted-foreground">{product?.name}</p>
                </div>
                <button type="button" onClick={onClose} aria-label="Хаах" className={`-mr-2 -mt-1 size-9 shrink-0 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${panelFocusRing}`}>
                  <X className="size-5" strokeWidth={1.25} />
                </button>
              </div>

              {hasOptions(product) ? (
                <div className="mt-6 flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-8 pb-6">
                  <OptionQuickAdd product={product} onAdd={id => { onAddToCart(id); onClose() }} />
                </div>
              ) : (
                <ul className="mt-6 flex-1 overflow-y-auto overscroll-contain px-4 sm:px-8 pb-6 divide-y divide-border">
                  {variants.map((variant: any) => {
                    const available = isAvailable(variant)
                    const image = variant?.images?.length > 0 ? formatImageURL(variant.images[0]) : formatImageURL('placeholder.png')
                    const vDiscounts = filterDiscounts(product?.discounts, !!session?.user?.id, variant.id)?.filter((d: any) => !(d.type === 'buyXgetY' && d.role === 'reward')) || []
                    const vEffective = getEffectivePrice(variant, vDiscounts, !!session?.user?.id)
                    const vStrike = getStrikethroughPrice(variant, vDiscounts, !!session?.user?.id)
                    const loyaltyDeal = getLoyaltyProductDiscount({ product, variant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: vDiscounts.length > 0 && isDiscountBetter(variant, vDiscounts, !!session?.user?.id) })
                    const displayEffective = loyaltyDeal ? Math.max(0, (vEffective ?? variant.price ?? 0) - loyaltyDeal.unitSaving) : vEffective
                    const displayStrike = loyaltyDeal ? (variant.price ?? 0) : vStrike

                    return (
                      <li key={variant.id}>
                        <button type="button" disabled={!available} onClick={() => select(variant)} className={`group w-full flex items-center gap-4 py-4 text-left disabled:cursor-not-allowed ${rowFocus}`}>
                          <span className="w-14 aspect-3/4 shrink-0 overflow-hidden bg-muted">
                            <img src={image} alt="" className={`size-full object-cover ${available ? '' : 'opacity-50'}`} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className={`block truncate text-[13px] ${available ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{variant.name}</span>
                            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] tabular-nums">
                              {displayEffective !== null ? (
                                <>
                                  <span>{displayEffective === 0 ? 'Үнэгүй' : formatPrice(displayEffective)}</span>
                                  <span className="text-xs line-through text-muted-foreground">{formatPrice(displayStrike)}</span>
                                </>
                              ) : (
                                <span>{formatPrice(variant.price)}</span>
                              )}
                              {loyaltyDeal && <span className="px-1.5 py-0.5 border border-foreground text-[11px] leading-none">Лояалти -{loyaltyDeal.percent}%</span>}
                            </span>
                            {product?.isTrackable && available && <span className="mt-1 block text-xs text-muted-foreground tabular-nums">{variant.stock} ширхэг үлдсэн</span>}
                          </span>
                          <span className="shrink-0 text-xs">
                            {available ? <span className={sweepOnGroup}>Сагсанд хийх</span> : <span className="text-muted-foreground">Дууссан</span>}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
